import {
  Box,
  Chip as MUIChip,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { useSnackbar } from 'notistack';
import type { ChangeEvent } from 'react';
import { useMemo, useState } from 'react';
import { selectEmail, selectUserId } from '@/entities/session';
import { useConfirmationDialog } from '@/shared/hooks';
import { useAppDispatch, useAppSelector } from '@/shared/model';
import { Checkbox, Loader } from '@/shared/ui';
import { RelativeTimestamp } from '@/shared/ui/RelativeTimestamp/RelativeTimestamp';
import { selectRoles } from '../../model/rolesSlice';
import { batchUpdateUsers } from '../../model/slice';
import type { User } from '../../model/types';
import { BulkActionsBar, type BulkChanges } from '../BulkActionsBar';
import { RoleLink } from '../RoleLink';
import { UserActionsMenu } from '../UserActionsMenu/UserActionsMenu';

const Chip = styled(MUIChip)(({ theme }) => ({
  height: 24,
  fontSize: '0.75rem',
  lineHeight: '0.875rem',
  fontWeight: 500,
  padding: `${theme.spacing(0.5)} ${theme.spacing(1)}`,

  '& .MuiChip-label': {
    padding: 0,
  },
}));

interface Props {
  users?: User[];
}

export function UsersTable({ users }: Props) {
  const dispatch = useAppDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const userId = useAppSelector(selectUserId);
  const userEmail = useAppSelector(selectEmail);
  const roles = useAppSelector(selectRoles);

  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [isSaving, setIsSaving] = useState(false);

  const sortedUsers = useMemo(() => {
    return users?.toSorted((a, b) => {
      if (a.email === userEmail) return -1;
      if (b.email === userEmail) return 1;
      return a.email.localeCompare(b.email);
    });
  }, [users, userEmail]);

  const selectableUsers = useMemo(
    () => (sortedUsers ?? []).filter((u) => u.id !== userId),
    [sortedUsers, userId],
  );

  const selectedCount = selectedIds.size;
  const allSelected =
    selectableUsers.length > 0 &&
    selectableUsers.every((u) => selectedIds.has(u.id));
  const someSelected = selectableUsers.some((u) => selectedIds.has(u.id));

  const applyBulkChanges = async (changes: BulkChanges) => {
    const selected = (sortedUsers ?? []).filter((u) => selectedIds.has(u.id));
    const update: { roleId?: string; isActive?: boolean } = {};
    if (changes.roleId !== undefined) update.roleId = changes.roleId;
    if (changes.isActive !== undefined) update.isActive = changes.isActive;

    setIsSaving(true);
    try {
      await dispatch(
        batchUpdateUsers({ updates: selected.map((user) => ({ user, update })) }),
      ).unwrap();
      setSelectedIds(new Set());

      const parts: string[] = [];
      if (changes.roleId !== undefined) parts.push('role assigned');
      if (changes.isActive !== undefined)
        parts.push(changes.isActive ? 'activated' : 'deactivated');
      enqueueSnackbar(
        `${selected.length} user${selected.length !== 1 ? 's' : ''} ${parts.join(' and ')}`,
        { variant: 'success' },
      );
    } catch {
      enqueueSnackbar('Unable to update some users.', {
        variant: 'error',
        autoHideDuration: 4000,
      });
      throw new Error('Bulk update failed');
    } finally {
      setIsSaving(false);
    }
  };

  const { openDialog: openDeactivateDialog, dialogElememnt: deactivateDialog } =
    useConfirmationDialog({
      slotProps: { dialog: { sx: { width: 370 } } },
      title: 'Deactivate Users',
      description: (ctx) => {
        const count = (ctx as { count: number })?.count;
        return (
          <>
            <Box>
              {count} {count === 1 ? 'user' : 'users'} will lose access until
              reactivated.
            </Box>
            <Box component="p" sx={{ mt: 2, mb: 0, fontWeight: 500 }}>
              Are you sure you want to do this?
            </Box>
          </>
        );
      },
      acceptButtonColor: 'error',
      acceptButtonText: 'Deactivate',
      onAccept: async (ctx) => {
        const { changes } = ctx as { count: number; changes: BulkChanges };
        await applyBulkChanges(changes);
      },
    });

  const handleBulkSave = (changes: BulkChanges) => {
    if (changes.isActive === false) {
      const activeCount = (sortedUsers ?? []).filter(
        (u) => selectedIds.has(u.id) && u.isActive,
      ).length;
      if (activeCount > 0) {
        openDeactivateDialog({ count: activeCount, changes });
        return;
      }
    }
    applyBulkChanges(changes).catch(() => {});
  };

  const handleToggleAll = (_event: ChangeEvent<HTMLInputElement>) => {
    if (allSelected) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(selectableUsers.map((u) => u.id)));
    }
  };

  const handleToggleRow = (id: string) => () => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  if (!sortedUsers) return null;

  return (
    <Box
      sx={{
        flexGrow: 1,
        display: 'flex',
        flexDirection: 'column',
        gap: 1,
        minHeight: 0,
      }}
    >
      {selectedCount > 0 && (
        <BulkActionsBar
          selectedCount={selectedCount}
          roles={roles ?? []}
          isSaving={isSaving}
          onSave={handleBulkSave}
          onCancel={() => setSelectedIds(new Set())}
        />
      )}

      <Box sx={{ flexGrow: 1, position: 'relative', minHeight: 0 }}>
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            right: 0,
            bottom: 0,
            left: 0,
            overflow: 'auto',
          }}
        >
          <Table stickyHeader sx={{ position: 'relative' }}>
            <TableHead>
              <TableRow
                sx={{
                  '& th': {
                    borderTop: '1px solid #DDDFE7',
                    borderRight: '1px solid #DDDFE7',

                    '&:first-of-type': {
                      borderTopLeftRadius: 8,
                      borderLeft: '1px solid #DDDFE7',
                    },

                    '&:last-of-type': {
                      borderTopRightRadius: 8,
                    },
                  },
                }}
              >
                <TableCell
                  sx={{ backgroundColor: '#F1F3F8', width: 44, pl: 1.5, pr: 0 }}
                >
                  <Checkbox
                    checked={allSelected}
                    indeterminate={!allSelected && someSelected}
                    onChange={handleToggleAll}
                    onClick={(e) => e.stopPropagation()}
                  />
                </TableCell>
                <TableCell sx={{ backgroundColor: '#F1F3F8' }}>Email</TableCell>
                <TableCell sx={{ backgroundColor: '#F1F3F8' }}>Role</TableCell>
                <TableCell sx={{ backgroundColor: '#F1F3F8' }}>
                  Status
                </TableCell>
                <TableCell sx={{ backgroundColor: '#F1F3F8' }}>
                  Last Active
                </TableCell>
                <TableCell sx={{ width: 52, backgroundColor: '#F1F3F8' }} />
              </TableRow>
            </TableHead>
            <TableBody>
              {sortedUsers.length === 0 && (
                <TableRow>
                  <TableCell
                    colSpan={6}
                    sx={{
                      textAlign: 'center',
                      borderRight: '1px solid #DDDFE7',
                      borderLeft: '1px solid #DDDFE7',
                      borderEndEndRadius: 8,
                      borderEndStartRadius: 8,
                    }}
                  >
                    No users found
                  </TableCell>
                </TableRow>
              )}
              {sortedUsers.map((user) => {
                const isSelected = selectedIds.has(user.id);
                const isCurrentUser = user.id === userId;
                return (
                  <TableRow
                    hover
                    role="checkbox"
                    tabIndex={-1}
                    key={user.id}
                    selected={isSelected}
                    sx={{
                      cursor: 'default',
                      '&.Mui-selected': { bgcolor: '#EDF0FF' },
                      '&.Mui-selected:hover': { bgcolor: '#E5E9FF' },
                      '&:last-of-type td': {
                        '&:first-of-type': {
                          borderBottomLeftRadius: 8,
                        },
                        '&:last-of-type': {
                          borderBottomRightRadius: 8,
                        },
                      },
                      '& td': {
                        borderRight: '1px solid #DDDFE7',
                        '&:first-of-type': {
                          borderLeft: '1px solid #DDDFE7',
                        },
                      },
                    }}
                  >
                    <TableCell sx={{ pl: 1.5, pr: 0, width: 44 }}>
                      {!isCurrentUser && (
                        <Checkbox
                          checked={isSelected}
                          onChange={handleToggleRow(user.id)}
                        />
                      )}
                    </TableCell>
                    <TableCell>{user.email}</TableCell>
                    <TableCell>
                      <RoleLink roleId={user.roleId} roleName={user.roleName} />
                    </TableCell>
                    <TableCell>
                      {user.isActive ? (
                        <Chip color="success" label="Active" />
                      ) : (
                        <Chip
                          sx={{ backgroundColor: '#F1F3F8', color: '#646874' }}
                          label="Deactivated"
                        />
                      )}
                    </TableCell>
                    <TableCell
                      sx={{
                        fontSize: '0.75rem',
                        lineHeight: '1.125rem',
                      }}
                    >
                      {user.lastActiveAt ? (
                        <RelativeTimestamp date={user.lastActiveAt} />
                      ) : (
                        '-'
                      )}
                    </TableCell>
                    <TableCell>
                      {!isCurrentUser && <UserActionsMenu user={user} />}
                    </TableCell>
                  </TableRow>
                );
              })}
              {isSaving && (
                <TableRow>
                  <TableCell>
                    <Loader active sx={{ position: 'absolute', zIndex: 2 }} />
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </Box>
      </Box>

      {deactivateDialog}
    </Box>
  );
}
